import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import {
  getConsultationsByNotary,
  createConsultation,
  updateConsultationStatus,
  getNotaryPendingActions,
} from '../../../services/notaryWorkspaceService';


const formatDate = (d) =>
  d ? new Date(d)?.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const formatDateTime = (d) =>
  d ? new Date(d)?.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

const MEETING_TYPE_CONFIG = {
  in_person: { label: 'Présentiel', icon: 'MapPin', color: 'text-brand-earth', bg: 'bg-brand-earth/10' },
  video: { label: 'Vidéo', icon: 'Video', color: 'text-blue-600', bg: 'bg-blue-50' },
  phone: { label: 'Téléphone', icon: 'Phone', color: 'text-green-600', bg: 'bg-green-50' },
};

const CONSULTATION_STATUS_CONFIG = {
  scheduled: { label: 'Planifiée', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  completed: { label: 'Terminée', bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
  cancelled: { label: 'Annulée', bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200' },
  rescheduled: { label: 'Reportée', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
};

const COMPLIANCE_STATUS_MAP = {
  nouveau: { score: 0, label: 'Non démarré', color: 'bg-slate-200' },
  instruction_en_cours: { score: 40, label: 'En cours', color: 'bg-blue-400' },
  dossier_valide: { score: 80, label: 'Validé', color: 'bg-green-500' },
  projet_acte_depose: { score: 90, label: 'Acte déposé', color: 'bg-purple-500' },
  transaction_cloturee: { score: 100, label: 'Clôturé', color: 'bg-brand-earth' },
};

// ─── Urgency Scoring Algorithm ────────────────────────────────────────────────
// Scores each dossier on multiple urgency signals, returns a numeric score (higher = more urgent)
// and a list of active urgency flags for display.

const URGENCY_LEVELS = {
  critical: { label: 'Critique', color: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500', icon: 'AlertTriangle', border: 'border-l-red-500' },
  high:     { label: 'Urgent',   color: 'bg-orange-100 text-orange-700 border-orange-200', dot: 'bg-orange-500', icon: 'AlertCircle', border: 'border-l-orange-500' },
  medium:   { label: 'Modéré',   color: 'bg-amber-100 text-amber-700 border-amber-200', dot: 'bg-amber-400', icon: 'Clock', border: 'border-l-amber-400' },
  low:      { label: 'Normal',   color: 'bg-slate-100 text-slate-600 border-slate-200', dot: 'bg-slate-300', icon: 'CheckCircle2', border: 'border-l-slate-200' },
};

/**
 * computeUrgency(dossier, consultations, pendingDocuments)
 * Returns { score: number, level: string, flags: string[] }
 *
 * Scoring signals:
 *  +60  — Consultation imminente (< 24h)
 *  +40  — Consultation dans les 48h
 *  +20  — Consultation dans les 72h
 *  +50  — Documents en retard (dossier en cours depuis > 7 jours, statut nouveau)
 *  +30  — Alerte conformité : dossier bloqué à 0% depuis > 14 jours
 *  +25  — Alerte conformité : progression faible (< 40%) depuis > 10 jours
 *  +15  — Plusieurs documents en attente liés à ce dossier (≥ 3)
 *  +10  — Dossier récent non démarré (< 3 jours)
 */
const computeUrgency = (dossier, consultations, pendingDocuments) => {
  let score = 0;
  const flags = [];
  const now = new Date();

  // ── Signal 1: Imminent consultation ──────────────────────────────────────
  const dossierConsultations = consultations?.filter(
    c => c?.dossier_id === dossier?.id && c?.status === 'scheduled' && new Date(c?.consultation_date) >= now
  ) || [];

  if (dossierConsultations?.length > 0) {
    const soonest = dossierConsultations?.reduce((a, b) =>
      new Date(a?.consultation_date) < new Date(b?.consultation_date) ? a : b
    );
    const hoursUntil = (new Date(soonest?.consultation_date) - now) / (1000 * 60 * 60);

    if (hoursUntil < 24) {
      score += 60;
      flags?.push(`Consultation dans ${Math.round(hoursUntil)}h`);
    } else if (hoursUntil < 48) {
      score += 40;
      flags?.push(`Consultation dans ${Math.round(hoursUntil)}h`);
    } else if (hoursUntil < 72) {
      score += 20;
      flags?.push(`Consultation dans ${Math.round(hoursUntil / 24)}j`);
    }
  }

  // ── Signal 2: Overdue document requests ──────────────────────────────────
  const dossierPendingDocs = pendingDocuments?.filter(
    doc => doc?.dossier?.id === dossier?.id || doc?.dossier_id === dossier?.id
  ) || [];

  const dossierAgeInDays = dossier?.created_at
    ? (now - new Date(dossier?.created_at)) / (1000 * 60 * 60 * 24)
    : 0;

  if (dossierPendingDocs?.length >= 3) {
    score += 15;
    flags?.push(`${dossierPendingDocs?.length} docs en attente`);
  } else if (dossierPendingDocs?.length > 0) {
    score += 8;
    flags?.push(`${dossierPendingDocs?.length} doc${dossierPendingDocs?.length > 1 ? 's' : ''} en attente`);
  }

  if (dossier?.status === 'nouveau' && dossierAgeInDays > 7) {
    score += 50;
    flags?.push(`Dossier non démarré (${Math.round(dossierAgeInDays)}j)`);
  } else if (dossier?.status === 'nouveau' && dossierAgeInDays <= 3) {
    score += 10;
    flags?.push('Nouveau dossier');
  }

  // ── Signal 3: Compliance red flags ───────────────────────────────────────
  const complianceScore = COMPLIANCE_STATUS_MAP?.[dossier?.status]?.score ?? 0;

  if (complianceScore === 0 && dossierAgeInDays > 14) {
    score += 30;
    flags?.push('Conformité bloquée à 0%');
  } else if (complianceScore < 40 && dossierAgeInDays > 10) {
    score += 25;
    flags?.push('Progression faible');
  }

  // ── Determine urgency level ───────────────────────────────────────────────
  let level = 'low';
  if (score >= 60) level = 'critical';
  else if (score >= 35) level = 'high';
  else if (score >= 15) level = 'medium';

  return { score, level, flags };
};

// ─── Sort Modes ───────────────────────────────────────────────────────────────
const SORT_MODES = [
  { id: 'urgency', label: 'Urgence', icon: 'Zap' },
  { id: 'recent', label: 'Récents', icon: 'Clock' },
  { id: 'progress', label: 'Progression', icon: 'TrendingUp' },
];

// ─── Urgency Badge ────────────────────────────────────────────────────────────
const UrgencyBadge = ({ level, flags }) => {
  const conf = URGENCY_LEVELS?.[level] || URGENCY_LEVELS?.low;
  if (level === 'low' && flags?.length === 0) return null;
  return (
    <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-system font-body-semibold ${conf?.color}`}>
      <Icon name={conf?.icon} size={10} />
      <span>{conf?.label}</span>
    </div>
  );
};

// ─── Urgency Flags Tooltip ────────────────────────────────────────────────────
const UrgencyFlags = ({ flags }) => {
  if (!flags?.length) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1.5">
      {flags?.map((flag, i) => (
        <span key={i} className="text-[9px] font-system text-text-secondary bg-slate-100 px-1.5 py-0.5 rounded-md">
          {flag}
        </span>
      ))}
    </div>
  );
};

// ─── Consultation Modal ───────────────────────────────────────────────────────
const ConsultationModal = ({ notaryId, dossiers, onClose, onCreated }) => {
  const [form, setForm] = useState({
    title: '',
    dossier_id: '',
    consultation_date: '',
    duration_minutes: 60,
    location: '',
    meeting_type: 'in_person',
    description: '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.title || !form?.consultation_date) return;
    setSaving(true);
    const { error } = await createConsultation({ ...form, notary_id: notaryId, dossier_id: form?.dossier_id || null });
    setSaving(false);
    if (!error) { onCreated?.(); onClose?.(); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Mobile drag handle */}
        <div className="flex justify-center pt-3 pb-1 sm:hidden">
          <div className="w-10 h-1 bg-slate-200 rounded-full" />
        </div>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border/50">
          <div className="flex items-center space-x-2.5">
            <div className="w-9 h-9 rounded-xl bg-brand-gold/10 flex items-center justify-center">
              <Icon name="CalendarPlus" size={18} className="text-brand-earth" />
            </div>
            <h3 className="text-ios-headline font-display font-display-semibold text-text-primary">Nouvelle consultation</h3>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors">
            <Icon name="X" size={16} className="text-text-secondary" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-ios-caption1 font-system font-body-semibold text-text-secondary mb-1.5 uppercase tracking-wider">Titre *</label>
            <input type="text" value={form?.title} onChange={e => setForm(f => ({ ...f, title: e?.target?.value }))}
              placeholder="Ex: Signature du compromis de vente"
              className="w-full px-4 py-3 border border-border rounded-xl text-ios-callout font-system focus:ring-2 focus:ring-brand-gold focus:border-transparent" required />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-ios-caption1 font-system font-body-semibold text-text-secondary mb-1.5 uppercase tracking-wider">Date & heure *</label>
              <input type="datetime-local" value={form?.consultation_date} onChange={e => setForm(f => ({ ...f, consultation_date: e?.target?.value }))}
                className="w-full px-4 py-3 border border-border rounded-xl text-ios-callout font-system focus:ring-2 focus:ring-brand-gold focus:border-transparent" required />
            </div>
            <div>
              <label className="block text-ios-caption1 font-system font-body-semibold text-text-secondary mb-1.5 uppercase tracking-wider">Durée (min)</label>
              <input type="number" value={form?.duration_minutes} onChange={e => setForm(f => ({ ...f, duration_minutes: parseInt(e?.target?.value) || 60 }))}
                min={15} max={480} step={15}
                className="w-full px-4 py-3 border border-border rounded-xl text-ios-callout font-system focus:ring-2 focus:ring-brand-gold focus:border-transparent" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-ios-caption1 font-system font-body-semibold text-text-secondary mb-1.5 uppercase tracking-wider">Type</label>
              <select value={form?.meeting_type} onChange={e => setForm(f => ({ ...f, meeting_type: e?.target?.value }))}
                className="w-full px-4 py-3 border border-border rounded-xl text-ios-callout font-system focus:ring-2 focus:ring-brand-gold focus:border-transparent bg-white">
                <option value="in_person">Présentiel</option>
                <option value="video">Vidéo</option>
                <option value="phone">Téléphone</option>
              </select>
            </div>
            <div>
              <label className="block text-ios-caption1 font-system font-body-semibold text-text-secondary mb-1.5 uppercase tracking-wider">Dossier lié</label>
              <select value={form?.dossier_id} onChange={e => setForm(f => ({ ...f, dossier_id: e?.target?.value }))}
                className="w-full px-4 py-3 border border-border rounded-xl text-ios-callout font-system focus:ring-2 focus:ring-brand-gold focus:border-transparent bg-white">
                <option value="">— Aucun —</option>
                {dossiers?.map(d => <option key={d?.id} value={d?.id}>{d?.reference} – {d?.property_title || 'Bien'}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-ios-caption1 font-system font-body-semibold text-text-secondary mb-1.5 uppercase tracking-wider">Lieu / Lien</label>
            <input type="text" value={form?.location} onChange={e => setForm(f => ({ ...f, location: e?.target?.value }))}
              placeholder="Adresse ou lien de réunion"
              className="w-full px-4 py-3 border border-border rounded-xl text-ios-callout font-system focus:ring-2 focus:ring-brand-gold focus:border-transparent" />
          </div>
          <div className="flex gap-3 pt-2 pb-safe">
            <button type="button" onClick={onClose}
              className="flex-1 py-3.5 border border-border rounded-xl text-ios-callout font-system font-body-medium text-text-secondary hover:bg-slate-50 active:bg-slate-100 transition-colors">
              Annuler
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 py-3.5 bg-brand-earth text-white rounded-xl text-ios-callout font-system font-body-semibold hover:bg-brand-earth/90 active:bg-brand-earth/80 disabled:opacity-60 transition-colors flex items-center justify-center space-x-2">
              {saving ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /><span>Création...</span></> : <><Icon name="Check" size={16} /><span>Créer</span></>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const NotaryWorkspace = ({ notaireProfile, dossiers, stats, onSelectDossier }) => {
  const [consultations, setConsultations] = useState([]);
  const [pendingActions, setPendingActions] = useState({ newDossiers: [], pendingDocuments: [] });
  const [showConsultationModal, setShowConsultationModal] = useState(false);
  const [loadingConsultations, setLoadingConsultations] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const [sortMode, setSortMode] = useState('urgency');

  const loadWorkspaceData = useCallback(async () => {
    if (!notaireProfile?.id) return;
    setLoadingConsultations(true);
    const [consultRes, actionsRes] = await Promise.all([
      getConsultationsByNotary(notaireProfile?.id),
      getNotaryPendingActions(notaireProfile?.id),
    ]);
    setConsultations(consultRes?.data || []);
    setPendingActions(actionsRes);
    setLoadingConsultations(false);
  }, [notaireProfile?.id]);

  useEffect(() => { loadWorkspaceData(); }, [loadWorkspaceData]);

  const upcomingConsultations = consultations?.filter(c =>
    c?.status === 'scheduled' && new Date(c?.consultation_date) >= new Date()
  )?.slice(0, 5);

  const totalPendingActions = (pendingActions?.newDossiers?.length || 0) + (pendingActions?.pendingDocuments?.length || 0);

  // ── Compute urgency for all dossiers ──────────────────────────────────────
  const dossiersWithUrgency = useMemo(() => {
    return (dossiers || [])?.map(d => ({
      ...d,
      _urgency: computeUrgency(d, consultations, pendingActions?.pendingDocuments || []),
    }));
  }, [dossiers, consultations, pendingActions?.pendingDocuments]);

  // ── Sort dossiers based on selected mode ──────────────────────────────────
  const sortedDossiers = useMemo(() => {
    const arr = [...dossiersWithUrgency];
    if (sortMode === 'urgency') {
      return arr?.sort((a, b) => (b?._urgency?.score || 0) - (a?._urgency?.score || 0));
    } else if (sortMode === 'recent') {
      return arr?.sort((a, b) => new Date(b?.created_at || 0) - new Date(a?.created_at || 0));
    } else if (sortMode === 'progress') {
      const scoreOf = d => COMPLIANCE_STATUS_MAP?.[d?.status]?.score ?? 0;
      return arr?.sort((a, b) => scoreOf(a) - scoreOf(b)); // lowest progress first (most needs attention)
    }
    return arr;
  }, [dossiersWithUrgency, sortMode]);

  // ── Urgency summary for overview ──────────────────────────────────────────
  const urgencySummary = useMemo(() => {
    const counts = { critical: 0, high: 0, medium: 0, low: 0 };
    dossiersWithUrgency?.forEach(d => { counts[d?._urgency?.level] = (counts?.[d?._urgency?.level] || 0) + 1; });
    return counts;
  }, [dossiersWithUrgency]);

  const criticalCount = urgencySummary?.critical + urgencySummary?.high;

  const SECTIONS = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: 'LayoutDashboard' },
    { id: 'cases', label: 'Dossiers', icon: 'FolderOpen', badge: stats?.nouveau },
    { id: 'actions', label: 'Actions', icon: 'AlertCircle', badge: totalPendingActions },
    { id: 'calendar', label: 'Calendrier', icon: 'Calendar', badge: upcomingConsultations?.length },
  ];

  return (
    <div className="space-y-4">
      {/* Section tabs — sticky on mobile for easy thumb reach */}
      <div className="sticky top-[60px] z-30 bg-surface/95 backdrop-blur-sm -mx-4 sm:mx-0 px-4 sm:px-0 py-2 sm:py-0 sm:static sm:bg-transparent sm:backdrop-blur-none">
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {SECTIONS?.map(s => (
            <button key={s?.id} onClick={() => setActiveSection(s?.id)}
              className={`flex-shrink-0 flex items-center space-x-2 px-4 py-3 sm:py-2 rounded-xl text-ios-callout font-system font-body-medium transition-all touch-manipulation ${
                activeSection === s?.id ? 'bg-brand-earth text-white shadow-ios-light' : 'bg-white border border-border/50 text-text-secondary hover:border-brand-gold/40 active:bg-slate-50'
              }`}>
              <Icon name={s?.icon} size={15} />
              <span>{s?.label}</span>
              {s?.badge > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-system font-body-semibold ${activeSection === s?.id ? 'bg-white/20 text-white' : 'bg-red-500 text-white'}`}>
                  {s?.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Overview */}
      {activeSection === 'overview' && (
        <div className="space-y-4">
          {/* Urgency summary banner */}
          {criticalCount > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-4 flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
                <Icon name="AlertTriangle" size={17} className="text-red-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-ios-callout font-system font-body-semibold text-red-800">
                  {criticalCount} dossier{criticalCount > 1 ? 's' : ''} nécessite{criticalCount > 1 ? 'nt' : ''} une attention immédiate
                </p>
                <p className="text-ios-caption1 text-red-600 mt-0.5">
                  {urgencySummary?.critical > 0 && `${urgencySummary?.critical} critique${urgencySummary?.critical > 1 ? 's' : ''}`}
                  {urgencySummary?.critical > 0 && urgencySummary?.high > 0 && ' · '}
                  {urgencySummary?.high > 0 && `${urgencySummary?.high} urgent${urgencySummary?.high > 1 ? 's' : ''}`}
                </p>
              </div>
              <button onClick={() => setActiveSection('cases')}
                className="text-[11px] font-system font-body-semibold text-red-700 hover:underline flex-shrink-0 py-1 touch-manipulation">
                Voir →
              </button>
            </div>
          )}

          {/* Compliance overview */}
          <div className="bg-white rounded-2xl border border-border/50 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-ios-callout font-system font-body-semibold text-text-primary flex items-center space-x-2">
                <Icon name="ShieldCheck" size={16} className="text-brand-earth" />
                <span>Statut de conformité des dossiers</span>
              </h3>
              {sortMode === 'urgency' && (
                <div className="flex items-center gap-1 text-[10px] font-system font-body-semibold text-brand-earth bg-brand-gold/10 px-2 py-1 rounded-lg">
                  <Icon name="Zap" size={10} />
                  <span>Trié par urgence</span>
                </div>
              )}
            </div>
            {sortedDossiers?.length === 0 ? (
              <p className="text-ios-caption1 text-text-secondary text-center py-4">Aucun dossier assigné</p>
            ) : (
              <div className="space-y-3">
                {sortedDossiers?.slice(0, 6)?.map(d => {
                  const compliance = COMPLIANCE_STATUS_MAP?.[d?.status] || COMPLIANCE_STATUS_MAP?.nouveau;
                  const urgencyConf = URGENCY_LEVELS?.[d?._urgency?.level] || URGENCY_LEVELS?.low;
                  return (
                    <button key={d?.id} onClick={() => onSelectDossier?.(d)}
                      className="w-full text-left group touch-manipulation active:opacity-80 transition-opacity">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center space-x-2 min-w-0 flex-1">
                          <span className="text-[11px] font-system font-body-semibold text-brand-earth bg-brand-gold/10 px-2 py-0.5 rounded-lg flex-shrink-0">{d?.reference}</span>
                          <span className="text-ios-caption1 text-text-primary font-system truncate group-hover:text-brand-earth transition-colors">{d?.property_title || 'Bien non renseigné'}</span>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                          {d?._urgency?.level !== 'low' && (
                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[9px] font-system font-body-semibold ${urgencyConf?.color}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${urgencyConf?.dot}`} />
                              {urgencyConf?.label}
                            </span>
                          )}
                          <span className="text-[10px] font-system font-body-semibold text-text-secondary">{compliance?.score}%</span>
                        </div>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${compliance?.color}`} style={{ width: `${compliance?.score}%` }} />
                      </div>
                      <p className="text-[10px] text-text-secondary font-system mt-0.5">{compliance?.label}</p>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Quick stats */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white rounded-2xl border border-border/50 p-4">
              <div className="flex items-center space-x-2 mb-2">
                <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center">
                  <Icon name="Clock" size={16} className="text-amber-600" />
                </div>
                <p className="text-ios-caption1 font-system text-text-secondary">Actions en attente</p>
              </div>
              <p className="text-ios-title2 font-display font-display-semibold text-text-primary">{totalPendingActions}</p>
              <p className="text-[10px] text-text-secondary font-system mt-0.5">{pendingActions?.newDossiers?.length} dossiers · {pendingActions?.pendingDocuments?.length} docs</p>
            </div>
            <div className="bg-white rounded-2xl border border-border/50 p-4">
              <div className="flex items-center space-x-2 mb-2">
                <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center">
                  <Icon name="Calendar" size={16} className="text-blue-600" />
                </div>
                <p className="text-ios-caption1 font-system text-text-secondary">Consultations à venir</p>
              </div>
              <p className="text-ios-title2 font-display font-display-semibold text-text-primary">{upcomingConsultations?.length}</p>
              <p className="text-[10px] text-text-secondary font-system mt-0.5">
                {upcomingConsultations?.[0] ? formatDateTime(upcomingConsultations?.[0]?.consultation_date) : 'Aucune planifiée'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Cases with compliance + urgency sorting */}
      {activeSection === 'cases' && (
        <div className="space-y-3">
          {/* Sort controls */}
          <div className="bg-white rounded-2xl border border-border/50 p-3">
            <div className="flex items-center justify-between mb-2.5">
              <div className="flex items-center gap-2">
                <Icon name="SlidersHorizontal" size={14} className="text-text-secondary" />
                <span className="text-ios-caption1 font-system font-body-semibold text-text-secondary uppercase tracking-wider">Trier par</span>
              </div>
              {sortMode === 'urgency' && criticalCount > 0 && (
                <div className="flex items-center gap-1 text-[10px] font-system font-body-semibold text-red-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                  {criticalCount} urgent{criticalCount > 1 ? 's' : ''}
                </div>
              )}
            </div>
            <div className="flex gap-2">
              {SORT_MODES?.map(mode => (
                <button key={mode?.id} onClick={() => setSortMode(mode?.id)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-ios-caption1 font-system font-body-semibold transition-all touch-manipulation ${
                    sortMode === mode?.id
                      ? 'bg-brand-earth text-white shadow-sm'
                      : 'bg-slate-50 text-text-secondary hover:bg-slate-100 active:bg-slate-200'
                  }`}>
                  <Icon name={mode?.icon} size={12} />
                  <span>{mode?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Urgency legend (shown only in urgency mode) */}
          {sortMode === 'urgency' && (
            <div className="flex gap-2 flex-wrap">
              {Object.entries(URGENCY_LEVELS)?.map(([key, conf]) => (
                <div key={key} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-[10px] font-system font-body-semibold ${conf?.color}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${conf?.dot}`} />
                  {conf?.label}
                </div>
              ))}
            </div>
          )}

          {sortedDossiers?.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl border border-border/50">
              <Icon name="FolderOpen" size={32} className="text-text-secondary mx-auto mb-3" />
              <p className="text-ios-callout font-system font-body-medium text-text-secondary">Aucun dossier assigné</p>
            </div>
          ) : sortedDossiers?.map((d, idx) => {
            const compliance = COMPLIANCE_STATUS_MAP?.[d?.status] || COMPLIANCE_STATUS_MAP?.nouveau;
            const urgencyConf = URGENCY_LEVELS?.[d?._urgency?.level] || URGENCY_LEVELS?.low;
            const isCritical = d?._urgency?.level === 'critical';
            const isHigh = d?._urgency?.level === 'high';
            return (
              <button key={d?.id} onClick={() => onSelectDossier?.(d)}
                className={`w-full text-left bg-white rounded-2xl border-l-4 border border-border/50 p-4 hover:shadow-ios-light active:bg-slate-50 transition-all touch-manipulation ${
                  isCritical ? 'border-l-red-500 bg-red-50/20' : isHigh ?'border-l-orange-500 bg-orange-50/10' :
                  d?._urgency?.level === 'medium'? 'border-l-amber-400' : 'border-l-slate-200'
                }`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      {/* Rank badge in urgency mode */}
                      {sortMode === 'urgency' && (
                        <span className="text-[9px] font-system font-body-semibold text-text-secondary bg-slate-100 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0">
                          {idx + 1}
                        </span>
                      )}
                      <span className="text-ios-caption1 font-system font-body-semibold text-brand-earth bg-brand-gold/10 px-2 py-0.5 rounded-lg">{d?.reference}</span>
                      {d?.is_verified_badge && <Icon name="BadgeCheck" size={14} className="text-green-600" />}
                      {d?._urgency?.level !== 'low' && (
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[9px] font-system font-body-semibold ${urgencyConf?.color}`}>
                          <Icon name={urgencyConf?.icon} size={9} />
                          {urgencyConf?.label}
                        </span>
                      )}
                    </div>
                    <p className="text-ios-callout font-system font-body-semibold text-text-primary truncate">{d?.property_title || 'Bien non renseigné'}</p>
                    <p className="text-ios-caption1 text-text-secondary truncate">{d?.property_address || '—'}</p>
                    {/* Urgency flags */}
                    <UrgencyFlags flags={d?._urgency?.flags} />
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <p className="text-ios-callout font-system font-body-semibold text-text-primary">{compliance?.score}%</p>
                    <p className="text-[10px] text-text-secondary">{compliance?.label}</p>
                  </div>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${compliance?.color}`} style={{ width: `${compliance?.score}%` }} />
                </div>
                {/* Mobile: show chevron for tap affordance */}
                <div className="flex items-center justify-between mt-2">
                  <span className="text-[10px] text-text-secondary font-system">{compliance?.label}</span>
                  <Icon name="ChevronRight" size={14} className="text-text-secondary" />
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Pending actions */}
      {activeSection === 'actions' && (
        <div className="space-y-4">
          {pendingActions?.newDossiers?.length > 0 && (
            <div className="bg-white rounded-2xl border border-border/50 p-4">
              <h4 className="text-ios-callout font-system font-body-semibold text-text-primary mb-3 flex items-center space-x-2">
                <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                <span>Nouveaux dossiers à accepter ({pendingActions?.newDossiers?.length})</span>
              </h4>
              <div className="space-y-2">
                {pendingActions?.newDossiers?.map(d => (
                  <button key={d?.id} onClick={() => onSelectDossier?.(d)}
                    className="w-full text-left flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-xl hover:bg-amber-100 active:bg-amber-200 transition-colors touch-manipulation">
                    <div className="min-w-0">
                      <p className="text-ios-callout font-system font-body-semibold text-amber-900 truncate">{d?.reference}</p>
                      <p className="text-ios-caption1 text-amber-700 truncate">{d?.property_title || 'Bien non renseigné'}</p>
                    </div>
                    <Icon name="ChevronRight" size={18} className="text-amber-600 flex-shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          )}
          {pendingActions?.pendingDocuments?.length > 0 && (
            <div className="bg-white rounded-2xl border border-border/50 p-4">
              <h4 className="text-ios-callout font-system font-body-semibold text-text-primary mb-3 flex items-center space-x-2">
                <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                <span>Documents à examiner ({pendingActions?.pendingDocuments?.length})</span>
              </h4>
              <div className="space-y-2">
                {pendingActions?.pendingDocuments?.map(doc => (
                  <div key={doc?.id} className="flex items-center justify-between p-4 bg-blue-50 border border-blue-200 rounded-xl">
                    <div className="min-w-0 flex-1">
                      <p className="text-ios-callout font-system font-body-semibold text-blue-900 truncate">{doc?.document_name}</p>
                      <p className="text-ios-caption1 text-blue-700">{doc?.dossier?.reference} · {doc?.document_type?.replace(/_/g, ' ')}</p>
                    </div>
                    <span className="text-[10px] font-system font-body-semibold text-blue-700 bg-blue-100 px-2 py-1 rounded-full flex-shrink-0 ml-2">En attente</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {totalPendingActions === 0 && (
            <div className="text-center py-12 bg-white rounded-2xl border border-border/50">
              <div className="w-12 h-12 rounded-2xl bg-green-50 flex items-center justify-center mx-auto mb-3">
                <Icon name="CheckCircle2" size={22} className="text-green-600" />
              </div>
              <p className="text-ios-callout font-system font-body-semibold text-green-700">Tout est à jour !</p>
              <p className="text-ios-caption1 text-text-secondary mt-1">Aucune action en attente.</p>
            </div>
          )}
        </div>
      )}

      {/* Calendar */}
      {activeSection === 'calendar' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-ios-headline font-display font-display-semibold text-text-primary">Consultations planifiées</h3>
            <button onClick={() => setShowConsultationModal(true)}
              className="flex items-center space-x-1.5 px-4 py-2.5 bg-brand-earth text-white rounded-xl text-ios-callout font-system font-body-semibold hover:bg-brand-earth/90 active:bg-brand-earth/80 transition-colors touch-manipulation">
              <Icon name="Plus" size={15} />
              <span>Planifier</span>
            </button>
          </div>

          {loadingConsultations ? (
            <div className="space-y-2.5">
              {[1, 2, 3]?.map(i => <div key={i} className="bg-slate-50 rounded-xl p-4 animate-pulse h-20" />)}
            </div>
          ) : consultations?.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl border border-dashed border-border">
              <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
                <Icon name="CalendarX" size={22} className="text-text-secondary" />
              </div>
              <p className="text-ios-callout font-system font-body-medium text-text-secondary">Aucune consultation planifiée</p>
              <button onClick={() => setShowConsultationModal(true)}
                className="mt-3 text-ios-callout font-system font-body-semibold text-brand-earth hover:underline py-2 px-4 touch-manipulation">
                Planifier une consultation →
              </button>
            </div>
          ) : (
            <div className="space-y-2.5">
              {consultations?.map(c => {
                const typeConf = MEETING_TYPE_CONFIG?.[c?.meeting_type] || MEETING_TYPE_CONFIG?.in_person;
                const statusConf = CONSULTATION_STATUS_CONFIG?.[c?.status] || CONSULTATION_STATUS_CONFIG?.scheduled;
                const isPast = new Date(c?.consultation_date) < new Date();
                const hoursUntil = (new Date(c?.consultation_date) - new Date()) / (1000 * 60 * 60);
                const isImminent = c?.status === 'scheduled' && hoursUntil > 0 && hoursUntil < 24;
                return (
                  <div key={c?.id} className={`bg-white rounded-xl border p-4 ${
                    isImminent ? 'border-red-200 bg-red-50/30' :
                    isPast && c?.status === 'scheduled'? 'border-amber-200 bg-amber-50/30' : 'border-border/50'
                  }`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start space-x-3 min-w-0 flex-1">
                        <div className={`w-10 h-10 rounded-xl ${typeConf?.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                          <Icon name={typeConf?.icon} size={17} className={typeConf?.color} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap mb-0.5">
                            <p className="text-ios-callout font-system font-body-semibold text-text-primary truncate">{c?.title}</p>
                            {isImminent && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-100 border border-red-200 text-[9px] font-system font-body-semibold text-red-700 flex-shrink-0">
                                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                                Imminente
                              </span>
                            )}
                          </div>
                          <p className="text-ios-caption1 text-text-secondary mt-0.5">{formatDateTime(c?.consultation_date)} · {c?.duration_minutes} min</p>
                          {c?.dossier?.reference && (
                            <p className="text-[10px] text-brand-earth font-system font-body-semibold mt-0.5">{c?.dossier?.reference}</p>
                          )}
                          {c?.location && <p className="text-[10px] text-text-secondary font-system mt-0.5 truncate">{c?.location}</p>}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        <span className={`text-[10px] font-system font-body-semibold px-2 py-0.5 rounded-full border ${statusConf?.bg} ${statusConf?.text} ${statusConf?.border}`}>
                          {statusConf?.label}
                        </span>
                        {c?.status === 'scheduled' && (
                          <button onClick={() => updateConsultationStatus(c?.id, 'completed')?.then(loadWorkspaceData)}
                            className="text-[11px] text-green-700 font-system font-body-semibold hover:underline py-1 px-2 touch-manipulation">
                            ✓ Terminée
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {showConsultationModal && (
        <ConsultationModal
          notaryId={notaireProfile?.id}
          dossiers={dossiers}
          onClose={() => setShowConsultationModal(false)}
          onCreated={loadWorkspaceData}
        />
      )}
    </div>
  );
};

export default NotaryWorkspace;
